import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import QRCode from 'qrcode';

interface QRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
}

export function QRCodeModal({ isOpen, onClose, roomId }: QRCodeModalProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (isOpen && roomId) {
      generateQRCode();
    }
  }, [isOpen, roomId]);

  const generateQRCode = async () => {
    setIsGenerating(true);
    try {
      const listenerUrl = `${window.location.origin}/listener/${roomId}`;
      const dataUrl = await QRCode.toDataURL(listenerUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M'
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Share Room</CardTitle>
            <CardDescription>
              Scan this QR code to join as a listener
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex justify-center">
            {isGenerating ? (
              <div className="w-64 h-64 bg-muted animate-pulse rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground">Generating...</span>
              </div>
            ) : qrCodeDataUrl ? (
              <img 
                src={qrCodeDataUrl} 
                alt="QR Code for room" 
                className="w-64 h-64 rounded-lg border"
              />
            ) : (
              <div className="w-64 h-64 bg-muted rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground">Failed to generate QR code</span>
              </div>
            )}
          </div>
          
          <div className="text-center space-y-2">
            <p className="text-sm text-muted-foreground">Room ID:</p>
            <p className="font-mono font-semibold text-lg">{roomId}</p>
          </div>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Or share this link: {window.location.origin}/listener/{roomId}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}